import { useState, useCallback } from "react";
import { TimeSlot, BookingData, Appointment, Company } from "../types";
import { publicApi } from "../services";

export const useAppointmentBooking = () => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null
  );
  const [bookingResult, setBookingResult] = useState<Appointment | null>(null);

  const fetchTimeSlots = useCallback(
    async (
      date: string,
      serviceId: number,
      professionalId: number,
      company: Company | null
    ) => {
      setLoading(true);
      setError(null);

      try {
        let response;

        // Use company-based slots for car services, professional-based for barbershops
        if (company?.category === "car") {
          response = await publicApi.getAvailableSlotsByCompany(
            serviceId,
            date
          );
        } else {
          // For barbershops or other categories, use professional-based slots
          response = await publicApi.getAvailableSlotsByProfessional(
            professionalId,
            serviceId,
            date
          );
        }

        if (response.success && response.data) {
          // For car companies, check if all slots have availableSpots: 0
          if (company?.category === "car" && response.data.length > 0) {
            const allSlotsUnavailable = response.data.every(
              (slot: any) => slot.availableSpots === 0
            );
            if (allSlotsUnavailable) {
              setTimeSlots([]);
              setError(
                "Não há vagas disponíveis para esta data. Por favor, escolha outro dia."
              );
              return;
            }
          }
          setTimeSlots(response.data);
        } else {
          setError(response.error || "Failed to load time slots");
        }
      } catch (err) {
        setError("Failed to load time slots");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const bookAppointment = useCallback(
    async (
      bookingData: BookingData,
      company: Company | null
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response = await publicApi.bookAppointment(bookingData);
        if (response.success && response.data) {
          setBookingResult(response.data);
          // Refresh time slots to update availability - use professionalId from booking data
          await fetchTimeSlots(
            bookingData.date,
            bookingData.serviceId,
            (bookingData as any).professionalId,
            company
          );
        } else {
          setError(response.error || "Failed to book appointment");
        }
      } catch (err) {
        setError("Failed to book appointment");
      } finally {
        setLoading(false);
      }
    },
    [fetchTimeSlots]
  );

  const resetBooking = useCallback(() => {
    setSelectedTimeSlot(null);
    setBookingResult(null);
    setError(null);
  }, []);

  return {
    timeSlots,
    loading,
    error,
    selectedDate,
    selectedTimeSlot,
    bookingResult,
    setSelectedDate,
    setSelectedTimeSlot,
    fetchTimeSlots,
    bookAppointment,
    resetBooking,
  };
};
